import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { tourComplete, tourCreate, tourNote, tourPatch } from "@shared/schemas/tours";
import { leadStatusChange } from "@shared/schemas/leads";
import { validateLead } from "@shared/validation/leads";
import { validateTour } from "@shared/validation/calendar";
import { blockingIssues, evaluation, parseInput, type ValidationAction } from "@shared/validation/issues";
import { useFormValidation } from "@web/lib/formValidation";
import { pointOccurrences } from "@web/lib/scheduleDraft";
import { recordValidationQuery } from "@web/lib/validation";
import { ApiError, errorMessage } from "@web/lib/api";
import { fmtLocal, fullName, joinLocal, splitLocal } from "@web/lib/format";
import { DISPOSITION_LABEL, tourStatusLabel, warningMessage } from "@web/lib/formatLeads";
import { membersQuery, venuesQuery } from "@web/lib/queries";
import { leadApi, type LeadDetail, type Tour } from "@web/lib/queries-leads";
import { useMe } from "@web/lib/session";
import { BottomSheet, Button, Inline, Picker, Row, RowGroup, SegmentedControl, Stack, Text, TextArea, TextField, Toggle, UnsavedChanges, ValidationSummary, useToast } from "@web/ui";

const DURATIONS = [
  { value: "30", label: "30 minutes" },
  { value: "45", label: "45 minutes" },
  { value: "60", label: "60 minutes" },
];

/** "conflicts with tour X at 2027-05-15T16:00" → a readable inline error. */
function conflictMessage(e: unknown): string | null {
  if (!(e instanceof ApiError) || e.status !== 409) return null;
  const at = /at (\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/.exec(e.message)?.[1];
  return at ? `Conflicts with another tour at ${fmtLocal(at)}. Pick a different time.` : "Conflicts with another tour at this venue.";
}

function useMemberOptions(keepId?: string | null) {
  const members = useQuery(membersQuery);
  return (members.data ?? []).filter((m) => m.active || m.id === keepId).map((m) => ({ value: m.id, label: m.name }));
}

/** Tour sheet (SPEC §6.1 item 5): venue / assignee / duration prefilled, native date + time, client confirmation toggle → POST /api/tours. */
export function TourSheet({ lead, onClose }: { lead: LeadDetail; onClose: () => void }) {
  const me = useMe();
  const toast = useToast();
  const venues = useQuery(venuesQuery());
  const memberOptions = useMemberOptions(lead.assignedMemberId);
  const [venueId, setVenueId] = useState(lead.venueId ?? "");
  const [assignee, setAssignee] = useState(lead.assignedMemberId ?? me.member.id);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [offset, setOffset] = useState<string | null>(null);
  const [duration, setDuration] = useState("30");
  const [notify, setNotify] = useState(Boolean(lead.contact.phone));
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const effectiveVenue = venueId || (venues.data?.length === 1 ? venues.data[0].id : "");
  const input = { leadId: lead.id, venueId: effectiveVenue, scheduledAt: joinLocal(date, time), offset, durationMin: Number(duration), assignedMemberId: assignee || null, notifyClient: notify, notes: notes.trim() || null };
  const local = validateTour({ ...input, timezone: me.organization.timezone }, { action: "tour.schedule" });
  const parsed = parseInput(tourCreate, input, { type: "tour" }, "tour.schedule");
  const validation = useFormValidation(evaluation([...local.issues, ...parsed.issues], false));
  const busy = validation.busy;
  const edit = (change: () => void) => validation.edit(() => { setError(null); change(); });
  const dirty = Boolean(date || time || notes || venueId !== (lead.venueId ?? "") || assignee !== (lead.assignedMemberId ?? me.member.id) || duration !== "30" || notify !== Boolean(lead.contact.phone));
  const occurrences = pointOccurrences({ date, time }, me.organization.timezone);

  async function save() {
    if (!parsed.success) { validation.report(parsed.issues); return; }
    if (!validation.begin("tour.schedule")) return;
    setError(null);
    try {
      const r = await leadApi.scheduleTour(parsed.data);
      toast.success(`Tour scheduled for ${fmtLocal(r.tour.scheduledAt)}`);
      const w = warningMessage(r.warning) ?? r.warning;
      if (w) toast.show(w);
      onClose();
    } catch (e) {
      validation.reject(e);
      setError(conflictMessage(e) ?? errorMessage(e));
    } finally {
      validation.finish();
    }
  }

  return (
    <><UnsavedChanges dirty={dirty} pending={busy} /><BottomSheet
      open
      onClose={() => validation.edit(onClose)}
      title="Schedule tour"
      footer={
        <Button loading={busy} onClick={save}>
          Schedule tour
        </Button>
      }
    >
      <Stack gap={3}>
        <Text variant="caption" tone="secondary">Timezone: {me.organization.timezone}</Text>
        <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
        <Picker label="Venue" placeholder="Choose a venue" value={effectiveVenue} {...validation.field("venueId")} onChange={value => edit(() => setVenueId(value))} options={(venues.data ?? []).map((v) => ({ value: v.id, label: v.name }))} />
        <Picker label="Assignee" placeholder="—" value={assignee} {...validation.field("assignedMemberId")} onChange={value => edit(() => setAssignee(value))} options={memberOptions} />
        <Inline gap={2} align="start">
          <TextField label="Date" type="date" className="flex-1" value={date} {...validation.field("scheduledAt")} onChange={event => edit(() => { setOffset(null); setDate(event.target.value); })} />
          <TextField label="Time" type="time" className="flex-1" value={time} disabled={busy} onChange={event => edit(() => { setOffset(null); setTime(event.target.value); })} />
        </Inline>
        {occurrences.length > 0 && <Picker label="Clock occurrence" placeholder="Choose which occurrence" value={offset ?? ""} options={occurrences} {...validation.field("offset")} onChange={value => edit(() => setOffset(value))} />}
        <Picker label="Duration" value={duration} {...validation.field("durationMin")} onChange={value => edit(() => setDuration(value))} options={DURATIONS} />
        <Toggle label="Text the client a confirmation" description={lead.contact.phone ? (lead.contact.smsConsent ? "Sends during 8am–9pm; otherwise queued for the next sending window" : "Needs SMS consent — not sent without it") : "No phone number on file"} checked={notify} disabled={!lead.contact.phone || busy} onChange={value => edit(() => setNotify(value))} />
        <TextArea label="Notes" placeholder="Parking, who to ask for, what to show" value={notes} {...validation.field("notes")} onChange={event => edit(() => setNotes(event.target.value))} />
        {error && <Text tone="danger">{error}</Text>}
      </Stack>
    </BottomSheet></>
  );
}

const DISPOSITIONS = [
  { key: "hot", label: "Hot" },
  { key: "warm", label: "Warm" },
  { key: "cold", label: "Cold" },
  { key: "not_a_fit", label: "Not a fit" },
] as const;
type Disposition = (typeof DISPOSITIONS)[number]["key"];
type Mode = "menu" | "complete" | "not_a_fit" | "no_show" | "reschedule" | "cancel";

/** Actions on one scheduled tour: Complete (disposition + notes; `not_a_fit` then offers Lost / Nurture), No-show, Reschedule, Cancel. */
export function TourActionsSheet({ lead, tour, onClose }: { lead: LeadDetail; tour: Tour; onClose: () => void }) {
  const toast = useToast();
  const me = useMe(), timezone = tour.timezone ?? me.organization.timezone;
  const savedChecks = useQuery(recordValidationQuery("tour", tour.id));
  const [mode, setMode] = useState<Mode>("menu");
  const [disposition, setDisposition] = useState<Disposition>("warm");
  const [notes, setNotes] = useState("");
  const [lostReason, setLostReason] = useState("");
  const [when, setWhen] = useState(() => splitLocal(tour.scheduledAt));
  const [duration, setDuration] = useState(String(tour.durationMin));
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState<string | null>(null);
  const occurrences = pointOccurrences(when, timezone);
  const title = `Tour · ${fmtLocal(tour.scheduledAt)}`;
  const scheduledAt = joinLocal(when.date, when.time);
  const rescheduleInput = { scheduledAt, durationMin: Number(duration), ...(scheduledAt !== tour.scheduledAt || offset ? { offset } : {}) };
  const parsedSchedule = parseInput(tourPatch, rescheduleInput, { type: "tour", id: tour.id }, "tour.save");
  const noteInput = { notes: notes.trim() || null };
  const parsedNote = parseInput(tourNote, noteInput, { type: "tour", id: tour.id }, "tour.save");
  const parsedComplete = parseInput(tourComplete, { ...noteInput, disposition }, { type: "tour", id: tour.id }, "tour.save");
  const parsedLost = parseInput(leadStatusChange, { status: "lost", lostReason: lostReason.trim() }, { type: "lead", id: lead.id }, "lead.transition");
  const scheduleFindings = validateTour({ ...tour, ...rescheduleInput, timezone, startsAtUtc: scheduledAt === tour.scheduledAt ? tour.startsAtUtc : undefined, endsAtUtc: undefined }, { before: tour, action: "tour.save" });
  const localIssues = mode === "reschedule" ? [...parsedSchedule.issues, ...scheduleFindings.issues]
    : mode === "complete" ? parsedComplete.issues : mode === "not_a_fit" || mode === "menu" ? [] : parsedNote.issues;
  const validation = useFormValidation(evaluation(localIssues, false));
  const busy = validation.busy;
  const edit = (change: () => void) => validation.edit(() => { setError(null); change(); });
  const dirty = Boolean(notes || lostReason || disposition !== "warm" || scheduledAt !== tour.scheduledAt || duration !== String(tour.durationMin) || offset);
  const close = () => validation.edit(onClose);
  const guard = <UnsavedChanges dirty={dirty} pending={busy} />;

  async function run(fn: () => Promise<unknown>, done: string, next?: () => void, action: ValidationAction = "tour.save") {
    if (!validation.begin(action)) return;
    setError(null);
    try {
      const result = await fn();
      toast.success(done);
      if (result && typeof result === "object" && "warning" in result && typeof result.warning === "string") toast.show(warningMessage(result.warning) ?? result.warning);
      // A concurrent scope change can leave the tour action successful while
      // its parent lead is no longer visible. Never offer another lead write.
      const parentUnavailable = result && typeof result === "object" && "lead" in result && result.lead === null;
      next && !parentUnavailable ? next() : onClose();
    } catch (e) {
      validation.reject(e);
      setError(conflictMessage(e) ?? errorMessage(e));
    } finally {
      validation.finish();
    }
  }

  const complete = () => parsedComplete.success ? run(() => leadApi.completeTour(lead.id, tour.id, parsedComplete.data), "Tour completed", disposition === "not_a_fit" ? () => { setNotes(""); setMode("not_a_fit"); validation.clear(); } : undefined) : validation.report(parsedComplete.issues);
  const markLost = () => {
    const result = validateLead({ ...lead, status: "lost", lostReason: lostReason.trim() }, { before: lead, action: "lead.transition", primaryContact: lead.contact, secondaryContact: lead.secondaryContact });
    if (!parsedLost.success || blockingIssues(result, "lead.transition").length) { validation.report([...parsedLost.issues, ...result.issues]); return; }
    return run(() => leadApi.setStatus(lead.id, "lost", parsedLost.data.lostReason), "Marked lost", undefined, "lead.transition");
  };
  const nurture = () => run(() => leadApi.setStatus(lead.id, "nurture"), "Moved to nurture", undefined, "lead.transition");
  const noShow = () => parsedNote.success ? run(() => leadApi.noShowTour(lead.id, tour.id, parsedNote.data.notes ?? undefined), "Marked as no-show") : validation.report(parsedNote.issues);
  const cancel = () => parsedNote.success ? run(() => leadApi.cancelTour(lead.id, tour.id, parsedNote.data.notes ?? undefined), "Tour cancelled") : validation.report(parsedNote.issues);
  const reschedule = () => {
    if (!parsedSchedule.success) { validation.report(parsedSchedule.issues); return; }
    return run(() => leadApi.patchTour(lead.id, tour.id, parsedSchedule.data), "Tour rescheduled");
  };

  if (mode === "menu")
    return (
      <>{guard}<BottomSheet open onClose={close} title={title}>
        <Stack gap={3}>
          <ValidationSummary result={savedChecks.data} loading={savedChecks.isFetching} unavailable={savedChecks.isError} onRetry={() => void savedChecks.refetch()} />
          <Text variant="callout" tone="secondary">
            {fullName(lead.contact) || "Client"} · {lead.venue?.name ?? ""} · {tour.durationMin} min · {tourStatusLabel(tour.status)}
            {tour.notes ? ` · ${tour.notes}` : ""}
          </Text>
          <RowGroup>
            <Row title="Complete" subtitle="Disposition + notes; lead becomes Toured" onClick={() => setMode("complete")} />
            <Row title="No-show" subtitle="Client didn't come" onClick={() => setMode("no_show")} />
            <Row title="Reschedule" subtitle="Change the time" onClick={() => setMode("reschedule")} />
            <Row title="Cancel tour" onClick={() => setMode("cancel")} />
          </RowGroup>
        </Stack>
      </BottomSheet></>
    );

  if (mode === "not_a_fit")
    return (
      <>{guard}<BottomSheet
        open
        onClose={close}
        title="Not a fit — what next?"
        footer={
          <Stack gap={2}>
            <Button variant="destructive" loading={busy} onClick={markLost}>
              Mark lost
            </Button>
            <Button variant="secondary" loading={busy} onClick={nurture}>
              Move to nurture
            </Button>
          </Stack>
        }
      >
        <Stack gap={3}>
          <ValidationSummary result={evaluation([...validation.result.issues, ...parsedLost.issues], false)} local unavailable={validation.unavailable} />
          <Text variant="caption" tone="secondary">Timezone: {timezone}{tour.timezone ? "" : " (legacy company timezone)"}</Text>
          <Text variant="callout" tone="secondary">
            The tour is saved as {DISPOSITION_LABEL.not_a_fit}. Close this sheet to leave the lead as Toured.
          </Text>
          <TextField label="Lost reason" placeholder="Budget, date taken, chose another venue…" value={lostReason} {...validation.field("lostReason")} onChange={event => edit(() => setLostReason(event.target.value))} />
          {error && <Text tone="danger">{error}</Text>}
        </Stack>
      </BottomSheet></>
    );

  if (mode === "reschedule")
    return (
      <>{guard}<BottomSheet
        open
        onClose={close}
        title="Reschedule tour"
        footer={
          <Button loading={busy} onClick={reschedule}>
            Save new time
          </Button>
        }
      >
        <Stack gap={3}>
          <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
          <Text variant="caption" tone="secondary">Timezone: {timezone}{tour.timezone ? "" : " (legacy company timezone)"}</Text>
          <Inline gap={2} align="start">
            <TextField label="Date" type="date" className="flex-1" value={when.date} {...validation.field("scheduledAt")} onChange={event => edit(() => { setOffset(null); setWhen({ ...when, date: event.target.value }); })} />
            <TextField label="Time" type="time" className="flex-1" value={when.time} disabled={busy} onChange={event => edit(() => { setOffset(null); setWhen({ ...when, time: event.target.value }); })} />
          </Inline>
          {occurrences.length > 0 && <Picker label="Clock occurrence" placeholder="Choose which occurrence" value={offset ?? ""} options={occurrences} {...validation.field("offset")} onChange={value => edit(() => setOffset(value))} />}
          <Picker label="Duration" value={duration} {...validation.field("durationMin")} onChange={value => edit(() => setDuration(value))} options={DURATIONS} />
          {error && <Text tone="danger">{error}</Text>}
        </Stack>
      </BottomSheet></>
    );

  const isComplete = mode === "complete";
  return (
    <>{guard}<BottomSheet
      open
      onClose={close}
      title={isComplete ? "Complete tour" : mode === "no_show" ? "No-show" : "Cancel tour"}
      footer={
        <Button variant={mode === "cancel" ? "destructive" : "primary"} loading={busy} onClick={isComplete ? complete : mode === "no_show" ? noShow : cancel}>
          {isComplete ? "Save disposition" : mode === "no_show" ? "Mark no-show" : "Cancel tour"}
        </Button>
      }
    >
      <Stack gap={3}>
        {isComplete && <SegmentedControl label="Disposition" items={DISPOSITIONS} value={disposition} disabled={busy} onChange={value => edit(() => setDisposition(value))} />}
        <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
        <TextArea label="Notes" placeholder={isComplete ? "How did it go? What do they need next?" : "Optional"} value={notes} {...validation.field("notes")} onChange={event => edit(() => setNotes(event.target.value))} autoFocus />
        {error && <Text tone="danger">{error}</Text>}
      </Stack>
    </BottomSheet></>
  );
}
